import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { CategoryVideo } from '@/model';
import { PutMediaCategoryVideoRequest } from '@/services/request/category-video';
import { usePutCategoryVideoMutation } from '@/services/queries/category-video';
import { useTranslation } from 'react-i18next';
export interface CategoryVideoEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: CategoryVideo;
  onClose: () => void;
  onDone?: () => void;
}

export const CategoryVideoEditModal = ({ isOpen, onClose, item, onDone }: CategoryVideoEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putItemMutation = usePutCategoryVideoMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: item.title,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      const payload: PutMediaCategoryVideoRequest = {
        title: data.title,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_title_label')}
              {...register('title')}
              helperText={t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_title_helper_text')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_category_video.componnets.category_video_edit_modal.category_edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
